import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { ENV } from "@app/env";
import { AngularFireDatabase } from "angularfire2/database";
import { AngularFirestore } from "angularfire2/firestore";
import * as firebase from "firebase/app";
import { Observable } from "rxjs/Observable";
import { catchError, retry } from "rxjs/operators";
import { map, switchMap } from "rxjs/operators";
import { IClient } from "../shared/models/client.model";
import { IEventQuestion } from "../shared/models/event-questions.model";
import { IPushMessage } from "../shared/models/messages.model";
import { HistoryData } from "./history";
import { _TRACKING_ENTITIES, TrackingProvider } from "./tracking";
import { Utilities } from "./utilities";

@Injectable()
export class EventData {
  private action: string = "update";

  constructor(
    private afDb: AngularFireDatabase,
    private historyData: HistoryData,
    private tracking: TrackingProvider,
    private http: HttpClient,
    private afs: AngularFirestore,
    private utilities: Utilities,
  ) { }

  public getEvents(client: string): any {
    return this.afDb.list(`Clients/${client}/EventList`).valueChanges();
  }

  public getEventsNoSubEvent(client: string): Observable<IClient[]> {
    return this.afDb.list<IClient>(
      `Clients/${client}/EventList`,
      (ref) => ref.orderByChild("is_sub_event").equalTo(false),
    ).valueChanges();
  }

  public getCategorieStaffData(clientId: string, categorieId: string) {
    return this.afDb.object<any>(`/Clients/${clientId}/StaffCategories/${categorieId}`)
      .valueChanges();
  }

  public getEventsSubEvent(client: string, eventId: string): Observable<IClient[]> {
    return this.afDb.list<IClient>(
      `Clients/${client}/EventList`,
      (ref) => ref.orderByChild("sub_event").equalTo(eventId),
    ).valueChanges();
  }

  public getEventInfo(client: string, eventId: string): Observable<any> {
    return this.afDb.object(`Clients/${client}/EventList/${eventId}`).valueChanges();
  }

  public getEventInfoSnap(client: string, eventId: string): Promise<any> {
    return this.afDb.object(`Clients/${client}/EventList/${eventId}`).query.once("value").then((snap) => snap.val());
  }

  public getEventUsers(client: string, id: string): Observable<any> {
    return this.afDb.list(`Clients/${client}/Events/${id}/Users`).valueChanges();
  }

  public getEventModules(client: string, id: string): any {
    return this.afDb
      .object(`Clients/${client}/EventConfig/${id}/modules`)
      .valueChanges();
  }

  // getEventModulesContent(client: string, id: string): any {
  //   return this.afDb
  //     .object(`Clients/${client}/EventConfig/${id}/modules_content`)
  //     .valueChanges();
  // }

  public getEventInteractivity(client: string, id: string): Observable<any> {
    return this.afDb
      .object(`Clients/${client}/EventConfig/${id}/interactivity`)
      .valueChanges();
  }

  public getEventModule(client: string, id: string, module: string): any {
    return this.afDb
      .object(`Clients/${client}/Events/${id}/${module}`)
      .valueChanges();
  }

  public getEventConfigModule(client: string, id: string, module: string): any {
    return this.afDb
      .object(`Clients/${client}/EventConfig/${id}/modules_config/${module}`)
      .valueChanges();
  }

  public getEventModuleList(client: string, id: string, module: string): any {
    return this.afDb
      .list(`Clients/${client}/Events/${id}/${module}`)
      .valueChanges();
  }

  public getEventModuleListSnap(client: string, id: string, module: string) {
    return this.afDb
      .list(`Clients/${client}/Events/${id}/${module}`).valueChanges();
  }

  public getEventMessages(
    client: string,
    id: string,
    module: "Alert" | "Toast" | "Push" | "Footer",
  ): Observable<any[]> {
    return this.afDb
      .list(`Clients/${client}/Events/${id}/Messages/${module}`)
      .valueChanges();
  }

  public getEventSecurity(client: string, id: string): any {
    return this.afDb
      .object(`Clients/${client}/EventList/${id}/permissions`)
      .valueChanges();
  }

  public getEventLanguage(client: string, id: string): any {
    return this.afDb
      .object(`Clients/${client}/EventList/${id}/defaultLanguage`)
      .valueChanges();
  }

  public getEventData(client: string, id: string): any {
    return this.afDb.object(`Clients/${client}/Events/${id}`).valueChanges();
  }

  public getEventCategories(clientId: string, id: string): any {
    return this.afDb.object(`Clients/${clientId}/EventConfig/${id}/categories`).valueChanges();
  }

  // transactionContentModule(
  //   client: string,
  //   id: string,
  //   module: string,
  //   state: boolean
  // ) {
  //   return firebase
  //     .database()
  //     .ref(`Clients/${client}/EventConfig/${id}/modules_content/${module}`)
  //     .transaction(current => {
  //       return state;
  //     });
  // }

  public transactionModule(
    client: string,
    id: string,
    module: string,
    state: boolean,
  ) {
    return firebase
      .database()
      .ref(`Clients/${client}/EventConfig/${id}/modules/${module}`)
      .transaction((current) => {
        return state;
      });
  }

  public createEventMessageStatitics(
    clientId: string,
    eventId: string,
    module: string,
    messageId: string,
  ): Observable<any> {
    return Observable.fromPromise(
      this.afs.firestore.collection(`${clientId}`)
        .doc("messages-event")
        .collection("event")
        .doc(eventId)
        .collection(module)
        .doc(`${messageId}`)
        .set({ views: 0 }));
  }

  public statisticEventMessageNodeExist(clientId: string, eventId: string, module: string, messageId: string) {
    return this.afs.collection(`${clientId}`)
      .doc("messages-event")
      .collection("event")
      .doc(eventId)
      .collection(module)
      .doc(`${messageId}`)
      .valueChanges();
  }

  public checkEventMessageStatisticNode(clientId: string, eventId: string, module: string, messageId: string) {
    return this.statisticEventMessageNodeExist(clientId, eventId, module, messageId).pipe(
      (switchMap((statistic) => {
        if (!statistic) {
          return this.createEventMessageStatitics(clientId, eventId, module, messageId);
        } else {
          return Observable.empty();
        }
      })),
    );
  }

  public transactionStateMessageModule(
    client: string,
    idEvent: string,
    idMessage: string,
    module: string,
    state: boolean,
  ) {
    return firebase
      .database()
      .ref(`Clients/${client}/Events/${idEvent}/Messages/${module}/${idMessage}/state`)
      .transaction((current) => {
        return state;
      });
  }

  public ofAllStateEventMessageModule(
    client: string,
    idEvent: string,
    module: string,
  ) {
    return firebase
      .database()
      .ref(`Clients/${client}/Events/${idEvent}/Messages/${module}/`)
      .orderByChild("state")
      .equalTo(true)
      .once("value")
      .then((snapshot) => snapshot.forEach((childSnapshot) =>
        firebase.database().ref(
          `Clients/${client}/Events/${idEvent}/Messages/${module}/${childSnapshot.key}`,
        ).update({ state: false }),
      ));
  }

  public createOrUpdateMessaje(
    client: string,
    idEvent: string,
    module: string,
    object: any,
    idMessage: string = "",
  ) {
    if (!idMessage) {
      idMessage = this.afDb.createPushId();
      this.action = "create";
    }

    object.id = idMessage;

    return new Promise((res, rej) => {
      this.afDb.database
        .ref(`Clients/${client}/Events/${idEvent}/Messages/${module}/${idMessage}`)
        .set(object)
        // TODO: Fix resolve .set to .update
        .then(() => {
          return res(idMessage);
        })
        .catch((e) => {
          return rej(e);
        });
    });
  }

  public createEventPushMessage(clientId: string, eventId: string, message: IPushMessage) {
    if (!message.id) {
      message.id = this.afDb.createPushId();
      this.action = "create";
    }

    return new Promise((resolve, reject) => {
      this.afDb.database
        .ref(`Clients/${clientId}/Events/${eventId}/Messages/Push/${message.id}`)
        .set({ ...message, clientId })
        // TODO: Fix resolve .set to .update
        .then(() => {
          return resolve();
        })
        .catch((error) => {
          console.error("A big error happen");
          return reject(error);
        });
    });
  }

  public sendEventPushMessage(clientConfig: IClient, eventId: string, message: IPushMessage) {
    return new Promise((resolve, reject) => {
      this.afDb.object(`Clients/${clientConfig.id}/Events/${eventId}/Messages/Push/${message.id}/sended`).set(true)
        .then(() => {
          this.http.post(`${ENV.backend}/notification/sendMessageToTopic`, {
            badge: clientConfig.style_image_badge,
            body: message.text[clientConfig.defaultLanguage],
            icon: clientConfig.style_image_icon,
            tag: message.id,
            title: message.title,
            topic: `/topics/${eventId}`,
            // "onclick": "www.rebus.com.co",
            // "params": `/?push_${entity.id}`,
          }).pipe(retry(3), catchError((err) => [
            this.utilities.consoleError("An error ocurred", err),
            reject(err),
          ])).subscribe((response) => {
            this.utilities.consoleDecorator("Message Sended", response);
            resolve();
          });
        });
    });
  }

  public setEventDataFirst(client: string, object: any, eventId: string = ""): any {
    if (!eventId) {
      eventId = this.afDb.createPushId();
      object.created_at = new Date().getTime();
      this.action = "create";
    } else {
      object.update_at = new Date().getTime();
    }

    object.id = eventId;

    return new Promise((res, rej) => {
      this.afDb.database
        .ref(`Clients/${client}/EventList/${eventId}`)
        .update(object)
        .then(() => {
          this.historyData.set(
            "General description",
            "event",
            this.action,
            object,
          );

          return res(eventId);
        })
        .catch((e) => {
          return rej(e);
        });
    });
  }

  public setEventDataInteractivity(client: string, object: any, eventId: string): any {
    return new Promise((res, rej) => {
      this.afDb.database
        .ref(`Clients/${client}/EventConfig/${eventId}/interactivity`)
        // TODO: Fix resolve .set to .update
        .set(object)
        .then(() => {
          this.historyData.set(
            "Interactivity",
            "event",
            this.action,
            object,
          );

          return res(eventId);
        })
        .catch((e) => {
          return rej(e);
        });
    });
  }

  public setEventRegisterCode(client: string, object: any, eventId: string): any {
    return new Promise((res, rej) => {
      this.afDb.database
        .ref(`Clients/${client}/EventUsers/${eventId}/RegisterCode`)
        // TODO: Fix resolve .set to .update
        .set(object)
        .then(() => {
          return res(eventId);
        })
        .catch((e) => {
          return rej(e);
        });
    });
  }

  public pushEventModule(
    client: string,
    object: any,
    eventId: string,
    module: string,
    id: string = "",
  ): any {
    if (id === "") {
      this.action = "create";
      id = this.afDb.createPushId();
      object.created_at = new Date().getTime();
    } else {
      object.update_at = new Date().getTime();
    }

    object.id = id;

    return new Promise((res, rej) => {
      this.afDb.database
        .ref(`Clients/${client}/Events/${eventId}/${module}/${id}`)
        .update(object)
        .then(() => {
          this.historyData.set(
            `Module ${module} content`,
            "event",
            this.action,
            object,
          );

          if (module === "News") {
            this.tracking.trackEntity(_TRACKING_ENTITIES.NEWS_EVENT, id, client, object);
          }

          if (module === "CommercialSamples") {
            this.tracking.trackEntity(_TRACKING_ENTITIES.EVENT_COMMERCIAL_SAMPLE, id, client, object);
          }

          return res(id);
        })
        .catch((e) => {
          return rej(e);
        });
    });
  }

  public setEventModule(
    client: string,
    object: any,
    eventId: string,
    module: string,
  ): any {
    return new Promise((res, rej) => {
      this.afDb.database
        .ref(`Clients/${client}/Events/${eventId}/${module}`)
        .update(object)
        .then(() => {
          this.historyData.set(
            `Module ${module} content`,
            "event",
            this.action,
            object,
          );

          return res(eventId);
        })
        .catch((e) => {
          return rej(e);
        });
    });
  }

  public setEventCategories(client: string, categories: any, eventId: string): Promise<any> {
    return this.afDb.database
      .ref(`Clients/${client}/EventConfig/${eventId}/categories`)
      .update(categories);
  }

  public updateEventAI(client: string, eventId: string, eventData: any): Promise<any> {
    return this.tracking.trackEntityPromise(_TRACKING_ENTITIES.EVENT, eventId, client, eventData);
  }

  public setEventConfigModule(
    client: string,
    object: any,
    eventId: string,
    module: string,
  ): any {
    return new Promise((res, rej) => {
      this.afDb.database
        .ref(`Clients/${client}/EventConfig/${eventId}/modules_config/${module}`)
        // TODO: Fix resolve .set to .update
        .set(object)
        .then(() => {
          return res(eventId);
        })
        .catch((e) => {
          return rej(e);
        });
    });
  }

  public createEventPollStatitics(clientId: string, eventId: string, pollId: string): Observable<any> {
    return Observable.fromPromise(
      this.afs.firestore.collection(`${clientId}`)
        .doc("statistics-event")
        .collection("event")
        .doc(eventId)
        .collection("poll")
        .doc(`${pollId}`)
        .set({ views: 0 }));
  }

  public statisticEventPollNodeExist(clientId: string, eventId: string, pollId: string) {
    return this.afs.collection(`${clientId}`)
      .doc("statistics-event")
      .collection("event")
      .doc(eventId)
      .collection("poll")
      .doc(`${pollId}`)
      .valueChanges();
  }

  public checkEventPollStatisticNode(clientId: string, eventId: string, pollId: string): Observable<any> {
    return this.statisticEventPollNodeExist(clientId, eventId, pollId)
      .pipe((switchMap((statistic) => {
        if (!statistic) {
          return this.createEventPollStatitics(clientId, eventId, pollId);
        }
        return Observable.of({});
      })),
      );
  }

  public setEventDataSecond(client: string, object: any, eventId: string): any {
    return new Promise((res, rej) => {
      this.afDb.database
        .ref(`Clients/${client}/EventConfig/${eventId}`)
        .update(object)
        .then(() => {
          this.historyData.set(`Modules state`, "event", this.action, object);

          return res(eventId);
        })
        .catch((e) => {
          return rej(e);
        });
    });
  }

  public setEventDataThirdh(client: string, object: any, eventId: string): any {
    return new Promise((res, rej) => {
      this.afDb.database
        .ref(`Clients/${client}/EventList/${eventId}/permissions`)
        // TODO: Fix resolve .set to .update
        .set(object)
        .then(() => {
          this.historyData.set(
            `Permissions state`,
            "event",
            this.action,
            object,
          );

          return res(eventId);
        })
        .catch((e) => {
          return rej(e);
        });
    });
  }

  public getQuestionsByEventId(clientId: string, eventId: string): Observable<IEventQuestion[]> {
    return this.afDb
      .list<IEventQuestion>(`Clients/${clientId}/Events/${eventId}/Questions/`)
      .valueChanges();
  }

  public getQuestionsByEventIdPublic(clientId: string, eventId: string): Observable<IEventQuestion[]> {
    return this.afDb
      .list<IEventQuestion>(`Clients/${clientId}/Events/${eventId}/Questions/`, (ref) => {
        return ref.orderByChild("public").equalTo(true);
      }).valueChanges();
  }

  public setQuestionByEventId(
    clientId: string,
    eventId: string,
    questionId: string,
    question: any,
  ) {
    question.created_at = new Date().getTime();
    return new Promise((res, rej) => {
      this.afDb.database
        .ref(`Clients/${clientId}/Events/${eventId}/Questions/${questionId}/`)
        .update(question)
        .then(() => {
          return res();
        })
        .catch((e) => {
          return rej(e);
        });
    });
  }

  public setQuestionArchiveByEventId(clientId: string, eventId: string, questionId: string) {
    return this.afDb.object(`/Clients/${clientId}/Events/${eventId}/Questions/${questionId}/archive`).set(true);
  }

  public unSetQuestionArchiveByEventId(clientId: string, eventId: string, questionId: string) {
    return this.afDb.object(`/Clients/${clientId}/Events/${eventId}/Questions/${questionId}/archive`).set(false);
  }

  public deleteQuestionEventId(clientId: string, eventId: string, questionId: string) {
    return this.afDb.object(`/Clients/${clientId}/Events/${eventId}/Questions/${questionId}`).remove();
  }


}
// end file



// WEBPACK FOOTER //
// ./src/providers/event.ts